import { ErrorBoundaryFatal } from "@/components/errors/ErrorBoundaryFatal";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { MainLayoutFallback } from "./layouts/MainLayoutFallback";
import Index from "./pages/Index";
import "./App.css";
import { trackPageView } from "./utils/analytics";
import { queryClient } from "./queries/client";

const Market = lazy(() => import("./pages/Market"));
const Cards = lazy(() => import("./pages/Cards"));
const VirtualBinder = lazy(() => import("./pages/VirtualBinder"));
const CollectionCoach = lazy(() => import("./pages/CollectionCoach"));
const Watchlist = lazy(() => import("./pages/Watchlist"));
const NewsUpdates = lazy(() => import("./pages/NewsUpdates"));
const NewsArticle = lazy(() => import("./pages/NewsArticle"));
const Scan = lazy(() => import("./pages/Scan"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Alerts = lazy(() => import("./pages/Alerts"));
const SharedPage = lazy(() => import("./pages/Shared"));
const NotFound = lazy(() => import("./pages/NotFound"));
const YouTubeAuthor = lazy(() => import("./pages/YouTubeAuthor"));
const SetDetails = lazy(() => import("./pages/SetDetails"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Sets = lazy(() => import("./pages/Sets"));
const AuthConfirmation = lazy(() => import("./pages/AuthConfirmation"));

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView(pathname + window.location.search);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <ErrorBoundaryFatal>
      <QueryClientProvider client={queryClient}>
        <SettingsProvider>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <ScrollToTop />
                <Suspense fallback={<MainLayoutFallback />}>
                  <Routes>
                    <Route path="/" element={<Index />} />
                    <Route path="/market" element={<Market />} />
                    <Route path="/sets/:id" element={<SetDetails />} />
                    <Route path="/cards" element={<Cards />} />
                    <Route path="/sets" element={<Sets />} />
                    <Route path="/virtual-binder" element={<VirtualBinder />} />
                    <Route
                      path="/collection-coach"
                      element={<CollectionCoach />}
                    />
                    <Route path="/watchlist" element={<Watchlist />} />
                    <Route path="/news" element={<NewsUpdates />} />
                    <Route path="/news/:slug" element={<NewsArticle />} />
                    <Route path="/scan" element={<Scan />} />
                    <Route path="/notifications" element={<Notifications />} />
                    <Route path="/alerts" element={<Alerts />} />
                    <Route path="/deal-hunter" element={<Alerts />} />
                    <Route path="/shared/:userId" element={<SharedPage />} />
                    <Route path="/youtube/:id" element={<YouTubeAuthor />} />
                    <Route path="/faq" element={<FAQ />} />
                    <Route
                      path="/auth/confirm"
                      element={<AuthConfirmation />}
                    />
                    <Route
                      path="/forgot-password"
                      element={<ForgotPassword />}
                    />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </SettingsProvider>
      </QueryClientProvider>
    </ErrorBoundaryFatal>
  );
}

export default App;
